import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Loading state for the bill workspace. It mirrors the real layout field for
 * field — label heights, input heights, the two-column grid, the document
 * pane — so the page does not reflow when data lands. Two grey slabs are
 * cheaper to write and worse to look at: they tell you nothing about what is
 * coming and shift everything when it arrives.
 */

function FieldSkeleton({ labelWidth = "w-20" }: { labelWidth?: string }) {
  return (
    <div className="space-y-1.5">
      <Skeleton className={`h-3.5 ${labelWidth}`} />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

export function BillDetailSkeleton() {
  return (
    <div className="space-y-4">
      {/* Header: back link, invoice title, status badge, queue nav, actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-28" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[55%_1fr]">
        <div className="space-y-4">
          {/* AI suggestions panel */}
          <Card className="space-y-2.5 p-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-40" />
              <div className="flex gap-2">
                <Skeleton className="h-7 w-16" />
                <Skeleton className="h-7 w-16" />
              </div>
            </div>
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-3">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3.5 flex-1" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </Card>

          {/* Field form */}
          <Card className="space-y-3 p-3">
            <div className="grid grid-cols-2 gap-3">
              <FieldSkeleton labelWidth="w-14" />
              <FieldSkeleton labelWidth="w-16" />
              <FieldSkeleton labelWidth="w-20" />
              <FieldSkeleton labelWidth="w-16" />
              <FieldSkeleton labelWidth="w-10" />
              <FieldSkeleton labelWidth="w-16" />
            </div>
            <div className="space-y-1.5">
              <Skeleton className="h-3.5 w-12" />
              <Skeleton className="h-20 w-full" />
            </div>
            <div className="flex justify-end">
              <Skeleton className="h-9 w-24" />
            </div>

            <Separator />

            {/* GL allocations */}
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-7 w-20" />
            </div>
            {[0, 1].map((row) => (
              <div key={row} className="flex items-center gap-2">
                <Skeleton className="h-9 flex-[2]" />
                <Skeleton className="h-9 flex-[2]" />
                <Skeleton className="h-9 flex-1" />
              </div>
            ))}
          </Card>

          {/* Activity timeline */}
          <Card className="space-y-2.5 p-3">
            <Skeleton className="h-4 w-20" />
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-start gap-2">
                <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </Card>
        </div>

        {/* Document pane */}
        <Card className="space-y-3 p-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-20" />
          </div>
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-[28rem] w-full" />
        </Card>
      </div>
    </div>
  );
}
